import type { DueTask } from "../types";

export interface CalendarOverdueBannerProps {
  items: DueTask[];
  onOpenTask: () => void;
}

/**
 * ADR-002 Amendment 5's A5-R1 ruling — a BINDING UI obligation, not a
 * suggestion: "Today and forward" projection stops after an overdue
 * cadence anchor (no fabricated forward pattern), so the calendar grid
 * alone would silently hide an overdue recurring chore's existence. The
 * ruling's own directive: "the calendar must surface overdue recurring
 * chores through a persistent overdue affordance (a banner/rail fed by
 * the same `/due` overdue... data), NOT by forward grid cells." Fed by
 * the viewer's own already-fetched `GET /due` `overdue` array (the same
 * data `app.tsx`'s `useDue()` already holds for Today/My list/Dashboard —
 * no second fetch) — deliberately NOT the Overdue Aging report
 * (`GET /reports/overdue`), which is a separate B-task outside this
 * screen's scope; flagged in the PR body.
 */
export function CalendarOverdueBanner({ items, onOpenTask }: CalendarOverdueBannerProps) {
  if (items.length === 0) return null;
  return (
    <div class="calendar-overdue-banner" role="region" aria-label="Overdue">
      <h2 class="calendar-overdue-heading">Still overdue</h2>
      <p class="calendar-overdue-hint">
        These won't show on upcoming days until they're done — open Today to mark one done.
      </p>
      <ul class="calendar-overdue-list">
        {items.map((task) => (
          <li key={task.id}>
            <button type="button" class="calendar-overdue-row" onClick={onOpenTask}>
              <span class="calendar-overdue-title">{task.title}</span>
              <span class="calendar-overdue-meta">{task.overdue_phrase ?? task.phrase}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
